import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withDisabledInitialNavigation } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { provideSvgIconsConfig } from '@ngneat/svg-icon';
import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { APP_ROUTES } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { RequestInterceptor } from './app/shared/interceptor/request.interceptor';
import { ResponseInterceptor } from './app/shared/interceptor/response.interceptor';
import { AlertService } from './app/shared/services/alert.service';
import { AzureAdService } from './app/shared/services/azure-ad.service';
import { GlobalErrorHandler } from './app/shared/services/globalErrorHandler.service';
import { GraphApiService } from './app/shared/services/graph-api.service';
import { ApplicationInsightsService } from './app/shared/services/telemetry.service';
import { SvgIcons } from './assets/images/icons/icons.module';
import { environment } from './environments/environment';
import {
  MODE_STORAGE_SERVICE,
  ModeLocalStorageService,
} from './lib/mode-toggle/mode-storage.service';
import { ModeToggleService } from './lib/mode-toggle/mode-toggle.service';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority:
        'https://login.microsoftonline.com/b7be7686-6f97-4db7-9081-a23cf09a96b5',
      redirectUri: environment.redirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {},
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    environment.scopes,
  ]);
  protectedResourceMap.set(environment.apiUrl, [environment.scopes]);
  protectedResourceMap.set('/api/', [environment.scopes]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.scopes],
    },
  };
}

const imports = [
  MsalModule.forRoot(
    new PublicClientApplication({
      auth: {
        clientId: environment.clientId,
        authority: environment.authority,
        redirectUri: environment.redirectUri, // This is your redirect URI
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: isIE, // Set to true for Internet Explorer 11
      },
    }),
    {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: [`${environment.scopes}`],
      },
    },
    {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map([
        [environment.apiUrl, [environment.scopes]],
        ['https://graph.microsoft.com/v1.0/me', [`${environment.scopes}`]],
      ]),
    }
  ),
];

if (environment.production) {
  enableProdMode();
}

const LaraLightBluePreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

const isIframe = window !== window.parent && !window.opener;
const routerProvider = isIframe
  ? provideRouter(APP_ROUTES, withDisabledInitialNavigation())
  : provideRouter(APP_ROUTES);

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(...imports),
    provideHttpClient(withInterceptorsFromDi()),
    routerProvider,
    provideSvgIconsConfig({
      sizes: {
        xs: '10px',
        sm: '12px',
        md: '16px',
        lg: '20px',
        xl: '24px',
        xxl: '30px',
      },
      icons: SvgIcons,
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: LaraLightBluePreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    ApplicationInsightsService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    ModeToggleService,
    {
      provide: MODE_STORAGE_SERVICE,
      useClass: ModeLocalStorageService,
    },
    MsalGuard,
    AzureAdService,
    GraphApiService,
    AlertService,
    DialogService,
  ],
}).catch(err => console.error(err));
